import { formatErrorMessage } from "../../../../../services/axios/APIError";
import { REGEX_ROUTE_TAG, type Season } from "@mysnowscape/snow-lib";

export type GroupedRoute = {
  _id: string;
  name: string;
  season: Season;
  order?: number;
  tags?: string[];
};

export type RouteGroupNode<T extends GroupedRoute = GroupedRoute> = {
  path: string;
  name: string;
  children: RouteGroupNode<T>[];
  routes: T[];
};

export function normalizeGroupPath(value: string): string {
  return value
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .join("/");
}

export function normalizeRouteTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const value of tags) {
    if (typeof value !== "string") continue;
    const path = normalizeGroupPath(value);
    if (!path || seen.has(path)) continue;
    seen.add(path);
    normalized.push(path);
  }
  return normalized;
}

export function isGroupPathOrDescendant(
  candidatePath: string,
  groupPath: string,
): boolean {
  const candidate = normalizeGroupPath(candidatePath);
  const group = normalizeGroupPath(groupPath);
  return Boolean(
    candidate &&
      group &&
      (candidate === group || candidate.startsWith(`${group}/`)),
  );
}

function validRouteTagValues(tags: unknown): string[] {
  return Array.isArray(tags)
    ? tags.filter(
        (tag): tag is string =>
          typeof tag === "string" && Boolean(normalizeGroupPath(tag)),
      )
    : [];
}

// localeCompare with options builds a new collator on every call, which takes
// most of a second to sort a few thousand properties. Share one instead.
const nameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/** Sort names the way people read them: "Crew 2" before "Crew 10". */
export function compareNames(a: string, b: string): number {
  return nameCollator.compare(a, b);
}

function compareRoutes<T extends GroupedRoute>(a: T, b: T): number {
  return (a.order ?? 0) - (b.order ?? 0) || compareNames(a.name, b.name);
}

/** Build the virtual Group hierarchy represented by slash-delimited route tags. */
export function buildRouteGroupTree<T extends GroupedRoute>(
  routes: T[],
  transientPaths: Iterable<string> = [],
): { groups: RouteGroupNode<T>[]; ungrouped: T[] } {
  const roots: RouteGroupNode<T>[] = [];
  const byPath = new Map<string, RouteGroupNode<T>>();

  const ensurePath = (rawPath: string) => {
    const path = normalizeGroupPath(rawPath);
    if (!path) return null;
    let parent: RouteGroupNode<T> | null = null;
    let currentPath = "";
    for (const name of path.split("/")) {
      currentPath = currentPath ? `${currentPath}/${name}` : name;
      let node = byPath.get(currentPath);
      if (!node) {
        node = { path: currentPath, name, children: [], routes: [] };
        byPath.set(currentPath, node);
        if (parent) parent.children.push(node);
        else roots.push(node);
      }
      parent = node;
    }
    return parent;
  };

  for (const path of Array.from(transientPaths)) ensurePath(path);

  const ungrouped: T[] = [];
  for (const route of routes) {
    const tags = normalizeRouteTags(route.tags);
    if (tags.length === 0) ungrouped.push(route);
    for (const tag of tags) ensurePath(tag)?.routes.push(route);
  }

  const sortNodes = (nodes: RouteGroupNode<T>[]) => {
    nodes.sort((a, b) => compareNames(a.name, b.name));
    for (const node of nodes) {
      node.routes.sort(compareRoutes);
      sortNodes(node.children);
    }
  };
  sortNodes(roots);
  ungrouped.sort(compareRoutes);
  return { groups: roots, ungrouped };
}

export function descendantRoutes<T extends GroupedRoute>(
  node: RouteGroupNode<T>,
): T[] {
  const byId = new Map(node.routes.map((route) => [route._id, route]));
  for (const child of node.children) {
    for (const route of descendantRoutes(child)) byId.set(route._id, route);
  }
  return Array.from(byId.values());
}

export function moveRouteGroupMembership(
  tags: unknown,
  sourcePath: string | null,
  targetPath: string | null,
): string[] {
  const existing = validRouteTagValues(tags);
  const source = sourcePath ? normalizeGroupPath(sourcePath) : null;
  const target = targetPath ? normalizeGroupPath(targetPath) : null;
  const sourceIndex = source
    ? existing.findIndex((tag) => normalizeGroupPath(tag) === source)
    : -1;
  const next = source
    ? existing.filter((tag) => normalizeGroupPath(tag) !== source)
    : existing;
  if (!target || next.some((tag) => normalizeGroupPath(tag) === target)) {
    return next;
  }
  if (sourceIndex >= 0)
    next.splice(Math.min(sourceIndex, next.length), 0, target);
  else next.push(target);
  return next;
}

export function renameGroupPathInTags(
  tags: unknown,
  oldPath: string,
  newPath: string,
): string[] {
  const oldPrefix = normalizeGroupPath(oldPath);
  const nextPrefix = normalizeGroupPath(newPath);
  const existing = validRouteTagValues(tags);
  return existing
    .map((tag) => {
      const normalized = normalizeGroupPath(tag);
      if (normalized === oldPrefix) return nextPrefix;
      if (isGroupPathOrDescendant(normalized, oldPrefix)) {
        return `${nextPrefix}${normalized.slice(oldPrefix.length)}`;
      }
      return tag;
    })
    .filter(
      (tag, index, list) =>
        list.findIndex(
          (candidate) =>
            normalizeGroupPath(candidate) === normalizeGroupPath(tag),
        ) === index,
    );
}

export function deleteGroupPathFromTags(tags: unknown, path: string): string[] {
  const prefix = normalizeGroupPath(path);
  return validRouteTagValues(tags).filter(
    (tag) => !isGroupPathOrDescendant(tag, prefix),
  );
}

export const GROUP_NAME_RULES =
  "Group names can only use letters, numbers, hyphens, and underscores. Spaces aren't allowed.";

export function groupSegmentError(
  rawName: string,
  siblingNames: Iterable<string>,
): string | null {
  const name = rawName.trim();
  if (!name) return "Enter a Group name.";
  if (name.includes("/")) return "Group names cannot contain “/”.";
  if (!REGEX_ROUTE_TAG.test(name)) return GROUP_NAME_RULES;
  const normalized = name.toLocaleLowerCase();
  if (
    Array.from(siblingNames).some(
      (sibling) => sibling.trim().toLocaleLowerCase() === normalized,
    )
  ) {
    return "That Group name is already used here.";
  }
  return null;
}

export function formatRouteGroupUpdateError(
  error: unknown,
  failedCount: number,
): string {
  let detail = formatErrorMessage(error);
  if (
    detail.includes("must match pattern") ||
    (detail.includes("Invalid string") && detail.includes("A-Za-z0-9_"))
  ) {
    detail = GROUP_NAME_RULES;
  } else if (!detail || detail === "Unknown Error") {
    detail = "Please try again.";
  }
  const prefix =
    failedCount === 1
      ? "The route could not be updated."
      : `${failedCount} routes could not be updated.`;
  return `${prefix} ${detail}`;
}

export function firstRouteGroup(tags: unknown): string {
  return normalizeRouteTags(tags)[0] ?? "Ungrouped";
}
